import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Tab } from '@headlessui/react';
import {
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChartBarIcon,
  PlusIcon,
  PresentationChartLineIcon,
  CurrencyDollarIcon,
  ReceiptPercentIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { ArrowPathRoundedSquareIcon } from '@heroicons/react/20/solid';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import BudgetTab from './finance/BudgetTab';
import RevenueTab from './finance/RevenueTab';
import ExpenseTab from './finance/ExpenseTab';
import TransactionModal from './finance/TransactionModal';

interface FinanceProps {
  tournamentId: string;
}

export default function Finance({ tournamentId }: FinanceProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [transactionType, setTransactionType] = useState<'income' | 'expense'>('income');
  const [loading, setLoading] = useState(true);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [totalBudget, setTotalBudget] = useState(0);
  const [totalRemaining, setTotalRemaining] = useState(0);
  const [revenueByCategory, setRevenueByCategory] = useState<Record<string, number>>({});
  const [expensesByCategory, setExpensesByCategory] = useState<Record<string, number>>({});
  const [monthlyData, setMonthlyData] = useState<any[]>([]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [playerRegistrationRevenue, setPlayerRegistrationRevenue] = useState(0);
  const [partnerContributions, setPartnerContributions] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [playerCount, setPlayerCount] = useState(0);
  const [partnerCount, setPartnerCount] = useState(0);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tournamentId) {
      fetchFinancialData();
      fetchPlayerRegistrationRevenue();
      fetchPartnerContributions();
    }
  }, [tournamentId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchFinancialData = async () => {
    try {
      setLoading(true);

      const { data: transactionsData, error: transactionsError } = await supabase
        .from('financial_transactions')
        .select('*')
        .eq('tournament_id', tournamentId)
        .eq('status', 'completed');

      if (transactionsError) throw transactionsError;

      if (transactionsData) {
        const revenueByCat: Record<string, number> = {};
        const expensesByCat: Record<string, number> = {};

        transactionsData.forEach(transaction => {
          if (transaction.type === 'income') {
            revenueByCat[transaction.category] = (revenueByCat[transaction.category] || 0) + transaction.amount;
          } else if (transaction.type === 'expense') {
            expensesByCat[transaction.category] = (expensesByCat[transaction.category] || 0) + transaction.amount;
          }
        });

        setRevenueByCategory(revenueByCat);
        setExpensesByCategory(expensesByCat);
        setTotalExpenses(Object.values(expensesByCat).reduce((sum, amt) => sum + amt, 0));
      }

      const { data: budgetData, error: budgetError } = await supabase
        .from('budget_items')
        .select('allocated_amount, spent_amount')
        .eq('tournament_id', tournamentId);

      if (!budgetError && budgetData) {
        const allocated = budgetData.reduce((sum, item) => sum + (item.allocated_amount || 0), 0);
        const spent = budgetData.reduce((sum, item) => sum + (item.spent_amount || 0), 0);
        setTotalBudget(allocated);
        setTotalRemaining(allocated - spent);
      }

      const { data: monthlyResult, error: monthlyError } = await supabase
        .from('monthly_financial_summary_view')
        .select('*')
        .eq('tournament_id', tournamentId)
        .order('month', { ascending: true });

      if (!monthlyError && monthlyResult) {
        setMonthlyData(monthlyResult);
      }

    } catch (err) {
      console.error('Error fetching financial data:', err);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayerRegistrationRevenue = async () => {
    try {
      const { data: tournamentData } = await supabase
        .from('tournaments')
        .select('registration_fee')
        .eq('id', tournamentId)
        .maybeSingle();

      const { data: playersWithPayment } = await supabase
        .from('players')
        .select('id')
        .eq('tournament_id', tournamentId)
        .contains('registration_status', { payment: true });

      const registrationFee = tournamentData?.registration_fee || 50000;
      const confirmedPlayers = playersWithPayment?.length || 0;

      setPlayerRegistrationRevenue(registrationFee * confirmedPlayers);
      setPlayerCount(confirmedPlayers);
    } catch (err) {
      console.error('Error calculating player registration revenue:', err);
    }
  };

  const fetchPartnerContributions = async () => {
    try {
      const { data: activePartners, error: partnersError } = await supabase
        .from('partners')
        .select('contribution')
        .eq('tournament_id', tournamentId)
        .eq('status', 'active');

      if (partnersError) throw partnersError;

      const totalContributions = activePartners?.reduce((sum, p) => sum + (p.contribution || 0), 0) || 0;
      const activePartnersCount = activePartners?.length || 0;

      setPartnerContributions(totalContributions);
      setPartnerCount(activePartnersCount);
    } catch (err) {
      console.error('Error fetching partner contributions:', err);
    }
  };

  const refreshAllRevenueData = async () => {
    try {
      setIsRefreshing(true);

      await Promise.all([
        fetchFinancialData(),
        fetchPlayerRegistrationRevenue(),
        fetchPartnerContributions()
      ]);

      toast.success('Revenue data refreshed successfully');
    } catch (err) {
      console.error('Error refreshing revenue data:', err);
      toast.error('Failed to refresh revenue data');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleAddTransaction = (type: 'income' | 'expense') => {
    setTransactionType(type);
    setSelectedTransaction(null);
    setIsModalOpen(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const exportFinancialData = async (type: string) => {
    try {
      setIsExporting(true);
      setShowExportDropdown(false);
      let data: any[] = [];
      let filename = '';
      let headers: string[] = [];

      switch (type) {
        case 'revenue': {
          const { data: revenueData, error: revenueError } = await supabase
            .from('financial_transactions')
            .select('*')
            .eq('tournament_id', tournamentId)
            .eq('type', 'income')
            .order('transaction_date', { ascending: false });

          if (revenueError) throw revenueError;
          data = revenueData || [];
          filename = 'revenue_transactions.csv';
          headers = ['id', 'description', 'category', 'amount', 'transaction_date', 'status', 'payment_method', 'reference_number', 'notes'];
          break;
        }
        case 'expenses': {
          const { data: expensesData, error: expensesError } = await supabase
            .from('financial_transactions')
            .select('*')
            .eq('tournament_id', tournamentId)
            .eq('type', 'expense')
            .order('transaction_date', { ascending: false });

          if (expensesError) throw expensesError;
          data = expensesData || [];
          filename = 'expense_transactions.csv';
          headers = ['id', 'description', 'category', 'amount', 'transaction_date', 'status', 'payment_method', 'reference_number', 'notes'];
          break;
        }
        case 'budget': {
          const { data: budgetData, error: budgetError } = await supabase
            .from('budget_items')
            .select('*')
            .eq('tournament_id', tournamentId)
            .order('category', { ascending: true });

          if (budgetError) throw budgetError;
          data = budgetData || [];
          filename = 'budget_items.csv';
          headers = ['id', 'category', 'subcategory', 'description', 'allocated_amount', 'spent_amount', 'fiscal_year', 'status'];
          break;
        }
        case 'summary': {
          const { data: summaryData, error: summaryError } = await supabase
            .from('financial_summary_view')
            .select('*')
            .eq('tournament_id', tournamentId);

          if (summaryError) throw summaryError;
          data = summaryData || [];
          filename = 'financial_summary.csv';
          headers = ['tournament_name', 'total_revenue', 'total_expenses', 'net_balance', 'sponsorship_revenue', 'registration_revenue', 'other_revenue', 'total_budget', 'budget_spent'];
          break;
        }
        default:
          throw new Error('Invalid export type');
      }

      const csvContent = [
        headers.join(','),
        ...data.map(row =>
          headers.map(header => {
            const value = row[header];
            if (value === null || value === undefined) return '';
            if (typeof value === 'object') return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
            if (typeof value === 'string' && value.includes(',')) return `"${value.replace(/"/g, '""')}"`;
            return value;
          }).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} data exported successfully`);
    } catch (err) {
      console.error(`Error exporting ${type} data:`, err);
      toast.error(`Failed to export ${type} data`);
    } finally {
      setIsExporting(false);
    }
  };

  const correctedRevenueByCategory: Record<string, number> = {};
  if (playerRegistrationRevenue > 0) {
    correctedRevenueByCategory['registration'] = playerRegistrationRevenue;
  }
  if (partnerContributions > 0) {
    correctedRevenueByCategory['sponsorship'] = partnerContributions;
  }
  Object.entries(revenueByCategory).forEach(([category, amount]) => {
    if (category !== 'registration' && category !== 'sponsorship') {
      correctedRevenueByCategory[category] = amount;
    }
  });
  const combinedRevenue = Object.values(correctedRevenueByCategory).reduce((sum, amt) => sum + amt, 0);
  const netBalance = combinedRevenue - totalExpenses;

  if (!tournamentId) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="max-w-md mx-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Tournament Selected</h3>
            <p className="text-gray-500 mb-6">
              Please select a tournament to view its financial data.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-golf-gray">Financial Management</h2>
        <div className="flex gap-2">
          <div className="relative" ref={exportRef}>
            <button
              className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center space-x-2"
              onClick={() => setShowExportDropdown(!showExportDropdown)}
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              <span>Export Data</span>
            </button>
            {showExportDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-200">
                <button
                  onClick={() => exportFinancialData('summary')}
                  disabled={isExporting}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Financial Summary
                </button>
                <button
                  onClick={() => exportFinancialData('revenue')}
                  disabled={isExporting}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Revenue Transactions
                </button>
                <button
                  onClick={() => exportFinancialData('expenses')}
                  disabled={isExporting}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Expense Transactions
                </button>
                <button
                  onClick={() => exportFinancialData('budget')}
                  disabled={isExporting}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Budget Items
                </button>
              </div>
            )}
          </div>
          <button
            onClick={() => handleAddTransaction('income')}
            className="modern-button px-4 py-2 text-white font-medium inline-flex items-center space-x-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Add Income</span>
          </button>
          <button
            onClick={() => handleAddTransaction('expense')}
            className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors inline-flex items-center space-x-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Add Expense</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="bg-green-500/10 p-3 rounded-xl">
              <ArrowTrendingUpIcon className="w-6 h-6 text-green-500" />
            </div>
          </div>
          <h3 className="text-sm text-gray-500 mb-1">Total Revenue</h3>
          <div className="text-2xl font-bold text-golf-gray flex items-center gap-2">
            {formatCurrency(combinedRevenue)}
            <button
              onClick={refreshAllRevenueData}
              disabled={isRefreshing}
              className={`p-1 text-gray-400 hover:text-golf-orange rounded-full transition-colors ${isRefreshing ? 'animate-spin' : ''}`}
              title="Refresh revenue data"
            >
              <ArrowPathRoundedSquareIcon className="w-4 h-4" />
            </button>
          </div>
          {playerRegistrationRevenue > 0 && (
            <div className="mt-1 text-xs text-gray-500">
              Incl. {formatCurrency(playerRegistrationRevenue)} from {playerCount} player{playerCount !== 1 ? 's' : ''}
            </div>
          )}
          {partnerContributions > 0 && (
            <div className="mt-1 text-xs text-gray-500">
              Incl. {formatCurrency(partnerContributions)} from {partnerCount} partner{partnerCount !== 1 ? 's' : ''}
            </div>
          )}
          {combinedRevenue > 0 && totalBudget > 0 && (
            <div className="mt-2 text-xs text-gray-500">
              {formatPercentage((combinedRevenue / totalBudget) * 100)} of target
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="bg-red-500/10 p-3 rounded-xl">
              <ArrowTrendingDownIcon className="w-6 h-6 text-red-500" />
            </div>
          </div>
          <h3 className="text-sm text-gray-500 mb-1">Total Expenses</h3>
          <div className="text-2xl font-bold text-golf-gray">
            {formatCurrency(totalExpenses)}
          </div>
          {totalExpenses > 0 && totalBudget > 0 && (
            <div className="mt-2 text-xs text-gray-500">
              {formatPercentage((totalExpenses / totalBudget) * 100)} of budget
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="bg-blue-500/10 p-3 rounded-xl">
              <ChartBarIcon className="w-6 h-6 text-blue-500" />
            </div>
          </div>
          <h3 className="text-sm text-gray-500 mb-1">Total Budget</h3>
          <div className="text-2xl font-bold text-golf-gray">
            {formatCurrency(totalBudget)}
          </div>
          {totalBudget > 0 && (
            <div className="mt-2 text-xs text-gray-500">
              {formatPercentage(totalRemaining / totalBudget * 100)} remaining
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="bg-emerald-500/10 p-3 rounded-xl">
              <BanknotesIcon className="w-6 h-6 text-emerald-500" />
            </div>
          </div>
          <h3 className="text-sm text-gray-500 mb-1">Net Balance</h3>
          <div className={`text-2xl font-bold ${netBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(netBalance)}
          </div>
          {combinedRevenue > 0 && (
            <div className="mt-2 text-xs text-gray-500">
              Profit margin: {formatPercentage(combinedRevenue > 0 ? (netBalance / combinedRevenue) * 100 : 0)}
            </div>
          )}
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <h3 className="text-lg font-semibold text-golf-gray mb-4">Revenue by Category</h3>
          <div className="space-y-4">
            {Object.entries(correctedRevenueByCategory).length > 0 ? (
              Object.entries(correctedRevenueByCategory).map(([category, amount]) => (
                <div key={category}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-gray-700 capitalize">{category}</span>
                    <span className="font-medium text-golf-gray">{formatCurrency(amount)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 rounded-full h-2"
                      style={{ width: `${combinedRevenue > 0 ? (amount / combinedRevenue) * 100 : 0}%` }}
                    />
                  </div>
                  <div className="flex justify-end text-xs text-gray-500 mt-1">
                    {formatPercentage(combinedRevenue > 0 ? (amount / combinedRevenue) * 100 : 0)}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No revenue data available
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
        >
          <h3 className="text-lg font-semibold text-golf-gray mb-4">Expenses by Category</h3>
          <div className="space-y-4">
            {Object.entries(expensesByCategory).length > 0 ? (
              Object.entries(expensesByCategory).map(([category, amount]) => (
                <div key={category}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-gray-700 capitalize">{category}</span>
                    <span className="font-medium text-golf-gray">{formatCurrency(amount)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-red-500 rounded-full h-2"
                      style={{ width: `${totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0}%` }}
                    />
                  </div>
                  <div className="flex justify-end text-xs text-gray-500 mt-1">
                    {formatPercentage(totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0)}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No expense data available
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 mb-6"
      >
        <h3 className="text-lg font-semibold text-golf-gray mb-4">Monthly Financial Trend</h3>
        <div className="h-64">
          {monthlyData.length > 0 ? (
            <div className="relative h-full">
              <div className="absolute inset-0 flex items-end">
                {monthlyData.map((month, index) => {
                  const revenue = month.monthly_revenue || 0;
                  const expenses = month.monthly_expenses || 0;
                  const maxValue = Math.max(...monthlyData.map(m => Math.max(m.monthly_revenue || 0, m.monthly_expenses || 0)));
                  const revenueHeight = maxValue > 0 ? (revenue / maxValue) * 100 : 0;
                  const expensesHeight = maxValue > 0 ? (expenses / maxValue) * 100 : 0;
                  const monthName = new Date(month.month).toLocaleString('default', { month: 'short' });

                  return (
                    <div key={index} className="flex-1 flex flex-col items-center justify-end h-full">
                      <div className="relative w-full px-1 flex justify-center items-end h-[calc(100%-30px)]">
                        <div
                          className="w-1/3 bg-green-500 rounded-t-sm"
                          style={{ height: `${revenueHeight}%` }}
                        />
                        <div
                          className="w-1/3 bg-red-500 rounded-t-sm ml-1"
                          style={{ height: `${expensesHeight}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 mt-2">{monthName}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              No monthly data available
            </div>
          )}
        </div>
        <div className="flex justify-center mt-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-sm mr-2"></div>
              <span className="text-sm text-gray-600">Revenue</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-sm mr-2"></div>
              <span className="text-sm text-gray-600">Expenses</span>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <Tab.Group selectedIndex={selectedTabIndex} onChange={setSelectedTabIndex}>
          <Tab.List className="flex border-b border-gray-200">
            <Tab className={({ selected }) =>
              `flex-1 py-4 px-6 text-center font-medium focus:outline-none transition-colors ${
                selected
                  ? 'text-golf-orange border-b-2 border-golf-orange'
                  : 'text-gray-500 hover:text-golf-gray hover:border-gray-300'
              }`
            }>
              <div className="flex items-center justify-center gap-2">
                <PresentationChartLineIcon className="w-5 h-5" />
                <span>Budget</span>
              </div>
            </Tab>
            <Tab className={({ selected }) =>
              `flex-1 py-4 px-6 text-center font-medium focus:outline-none transition-colors ${
                selected
                  ? 'text-golf-orange border-b-2 border-golf-orange'
                  : 'text-gray-500 hover:text-golf-gray hover:border-gray-300'
              }`
            }>
              <div className="flex items-center justify-center gap-2">
                <CurrencyDollarIcon className="w-5 h-5" />
                <span>Revenue</span>
              </div>
            </Tab>
            <Tab className={({ selected }) =>
              `flex-1 py-4 px-6 text-center font-medium focus:outline-none transition-colors ${
                selected
                  ? 'text-golf-orange border-b-2 border-golf-orange'
                  : 'text-gray-500 hover:text-golf-gray hover:border-gray-300'
              }`
            }>
              <div className="flex items-center justify-center gap-2">
                <ReceiptPercentIcon className="w-5 h-5" />
                <span>Expenses</span>
              </div>
            </Tab>
          </Tab.List>
          <Tab.Panels>
            <Tab.Panel>
              <BudgetTab
                tournamentId={tournamentId}
                onRefresh={fetchFinancialData}
              />
            </Tab.Panel>
            <Tab.Panel>
              <RevenueTab
                tournamentId={tournamentId}
                onRefresh={fetchFinancialData}
                onAddTransaction={() => handleAddTransaction('income')}
              />
            </Tab.Panel>
            <Tab.Panel>
              <ExpenseTab
                tournamentId={tournamentId}
                onRefresh={fetchFinancialData}
                onAddTransaction={() => handleAddTransaction('expense')}
              />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>

      <TransactionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTransaction(null);
        }}
        transaction={selectedTransaction}
        tournamentId={tournamentId}
        transactionType={transactionType}
        onSuccess={fetchFinancialData}
      />
    </div>
  );
}
